
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { SupplierModel, GlobalDefaults, SupplierParams } from '@/models/supplier';
import { ReorderSettingParam } from '@/models/reorder_setting_param';

interface VFormRef {
  validate(): boolean | Promise<boolean>;
  reset(): void;
  resetValidation(): void;
}

// Interface to track which fields are checked for force update
interface CheckedFields {
  leadtime: boolean;
  orderCycle: boolean;
  minStockCoverDays: boolean;
  minOrderValue: boolean;
  maxOrderValue: boolean;
  minOrderQuantity: boolean;
  maxOrderQuantity: boolean;
  scope: boolean;
}

@Component
export default class SupplierEditDialog extends Vue {
  @Prop({ default: false })
  public visible!: boolean;

  @Prop({ default: null })
  public supplier!: SupplierModel | null;

  @Prop({ default: false })
  public loading!: boolean;

  @Prop({ default: (): GlobalDefaults => ({}) })
  public globalDefaults!: GlobalDefaults;

  @Prop({ default: false })
  public skipConfirmation!: boolean;

  @Prop({ default: true })
  public showForceUpdate!: boolean;

  private params = {
    leadtime: 0,
    orderCycle: 0,
    minStockCoverDays: 0,
    minOrderValue: 0,
    maxOrderValue: 0,
    minOrderQuantity: 0,
    maxOrderQuantity: 0,
    scope: 'variant' as 'product' | 'variant'
  };

  private forceUpdate: boolean = false;
  private isFormValid: boolean = false;
  private confirmDialogVisible: boolean = false;
  private forceUpdateConfirmed: boolean = false;

  // Track which fields are checked for force update
  private checkedFields: CheckedFields = {
    leadtime: false,
    orderCycle: false,
    minStockCoverDays: false,
    minOrderValue: false,
    maxOrderValue: false,
    minOrderQuantity: false,
    maxOrderQuantity: false,
    scope: false,
  };

  private scopeOptions = [
    { text: 'Product', value: 'product', icon: 'mdi-package-variant' },
    { text: 'Variant', value: 'variant', icon: 'mdi-shape' }
  ];

  public numberRule: Array<(v: string | number | null | undefined) => boolean | string> = [
    (v: string | number | null | undefined) => {
      // Handle null, undefined, or empty string as valid (optional field)
      if (v === null || v === undefined || v === '') return true;
      // Convert to number and validate
      const num = typeof v === 'number' ? v : Number(v);
      return (!isNaN(num) && num >= 0) || "Value must be a non-negative number";
    },
  ];

  // Validation rule for max order value: must be >= min order value
  public get maxOrderValueRule(): Array<(v: string | number | null | undefined) => boolean | string> {
    return [
      ...this.numberRule,
      (v: string | number | null | undefined) => {
        if (v === null || v === undefined || v === '') return true;
        const max = typeof v === 'number' ? v : Number(v);
        const min = this.params.minOrderValue || 0;
        return max >= min || "Max order value must be greater than or equal to min order value";
      }
    ];
  }

  @Watch('supplier', { immediate: true, deep: true })
  private onSupplierChanged(newSupplier: SupplierModel | null) {
    if (newSupplier) {
      // Validate scope is one of the allowed values
      const validScope = newSupplier.scope === 'product' || newSupplier.scope === 'variant'
        ? newSupplier.scope
        : 'variant';

      this.params = {
        leadtime: newSupplier.leadTime || 0,
        orderCycle: newSupplier.reorderCycle || 0,
        minStockCoverDays: newSupplier.minStockCoverDays || 0,
        minOrderValue: newSupplier.minOrderValue || 0,
        maxOrderValue: newSupplier.maxOrderValue || 0,
        minOrderQuantity: newSupplier.minOrderQuantity || 0,
        maxOrderQuantity: newSupplier.maxOrderQuantity || 0,
        scope: validScope
      };
    }
  }

  @Watch('visible')
  private onVisibleChanged(newVal: boolean) {
    if (!newVal) {
      // Reset form when dialog closes
      this.forceUpdate = false;
      this.confirmDialogVisible = false;
      this.forceUpdateConfirmed = false;
      this.resetCheckedFields();
    }
  }

  @Watch('forceUpdate')
  private onForceUpdateChanged(newVal: boolean) {
    if (!newVal) {
      // Reset checked fields when force update is disabled
      this.resetCheckedFields();
      this.forceUpdateConfirmed = false;
    }
  }

  // Computed: check if any field is checked for force update
  get hasAnyCheckedFields(): boolean {
    return this.checkedFields.leadtime ||
           this.checkedFields.orderCycle ||
           this.checkedFields.minStockCoverDays ||
           this.checkedFields.minOrderValue ||
           this.checkedFields.maxOrderValue ||
           this.checkedFields.minOrderQuantity ||
           this.checkedFields.maxOrderQuantity ||
           this.checkedFields.scope;
  }

  // Check if a specific param should be shown in confirmation
  private shouldShowParam(field: keyof CheckedFields): boolean {
    if (this.forceUpdate) {
      // In force update mode, only show checked fields
      return this.checkedFields[field];
    }
    // In normal mode, show all fields
    return true;
  }

  // Check if there are time params to show
  get hasTimeParamsToShow(): boolean {
    return this.shouldShowParam('leadtime') ||
           this.shouldShowParam('orderCycle') ||
           this.shouldShowParam('minStockCoverDays');
  }

  // Check if there are order value params to show (only for suppliers page)
  get hasValueParamsToShow(): boolean {
    if (this.showForceUpdate) {
      return false; // Don't show order values on settings page
    }
    return this.shouldShowParam('minOrderValue') ||
           this.shouldShowParam('maxOrderValue');
  }

  // Check if there are quantity params to show
  get hasQuantityParamsToShow(): boolean {
    return this.shouldShowParam('minOrderQuantity') ||
           this.shouldShowParam('maxOrderQuantity');
  }

  // Format number with thousands separator
  private formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '0';
    }
    // Use Intl.NumberFormat for consistent formatting
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }

  private resetCheckedFields() {
    this.checkedFields = {
      leadtime: false,
      orderCycle: false,
      minStockCoverDays: false,
      minOrderValue: false,
      maxOrderValue: false,
      minOrderQuantity: false,
      maxOrderQuantity: false,
      scope: false,
    };
  }

  private handleCancel() {
    this.forceUpdate = false;
    this.confirmDialogVisible = false;
    this.$emit('cancel');
  }

  private async showConfirmation() {
    const form = this.$refs.form as VFormRef | undefined;

    // Type guard to ensure form exists and has validate function
    if (!form || typeof form.validate !== 'function') {
      return;
    }

    const isValid = await form.validate();
    if (!isValid) {
      return;
    }

    if (this.forceUpdate && !this.hasAnyCheckedFields) {
      return; // Button should be disabled, but just in case
    }

    // Skip internal confirmation dialog if parent handles confirmation
    if (this.skipConfirmation) {
      this.handleConfirmedSave();
      return;
    }

    // Reset confirmation checkbox and show dialog
    this.forceUpdateConfirmed = false;
    this.confirmDialogVisible = true;
  }

  private handleConfirmedSave() {
    if (this.forceUpdate) {
      // Force Update Mode: use checked fields only
      // This prevents overwriting existing variant values with unchecked params
      if (!this.hasAnyCheckedFields) {
        return;
      }

      // Create param object with only checked fields
      const checkedParams = new ReorderSettingParam();
      checkedParams.supplierId = this.supplier?.id || 0;

      if (this.checkedFields.leadtime) {
        checkedParams.leadtime = this.params.leadtime || 0;
      }
      if (this.checkedFields.orderCycle) {
        checkedParams.orderCycle = this.params.orderCycle || 0;
      }
      if (this.checkedFields.minStockCoverDays) {
        checkedParams.minStockCoverDays = this.params.minStockCoverDays || 0;
      }
      if (this.checkedFields.minOrderValue) {
        checkedParams.minOrderValue = this.params.minOrderValue || 0;
      }
      if (this.checkedFields.maxOrderValue) {
        checkedParams.maxOrderValue = this.params.maxOrderValue || 0;
      }
      if (this.checkedFields.minOrderQuantity) {
        checkedParams.minOrderQuantity = this.params.minOrderQuantity || 0;
      }
      if (this.checkedFields.maxOrderQuantity) {
        checkedParams.maxOrderQuantity = this.params.maxOrderQuantity || 0;
      }
      if (this.checkedFields.scope) {
        checkedParams.scope = this.params.scope || 'variant';
      }

      this.$emit('save', {
        params: checkedParams,
        forceUpdate: true
      });
    } else {
      // Normal Mode: send all params to update supplier record
      const reorderParam = new ReorderSettingParam();
      reorderParam.supplierId = this.supplier?.id || 0;
      reorderParam.leadtime = this.params.leadtime || 0;
      reorderParam.orderCycle = this.params.orderCycle || 0;
      reorderParam.minStockCoverDays = this.params.minStockCoverDays || 0;
      // Only include order value fields when they're visible (suppliers page, not settings page)
      if (!this.showForceUpdate) {
        reorderParam.minOrderValue = this.params.minOrderValue || 0;
        reorderParam.maxOrderValue = this.params.maxOrderValue || 0;
      }
      reorderParam.minOrderQuantity = this.params.minOrderQuantity || 0;
      reorderParam.maxOrderQuantity = this.params.maxOrderQuantity || 0;
      reorderParam.scope = this.params.scope || 'variant';

      this.$emit('save', {
        params: reorderParam,
        forceUpdate: false
      });
    }

    // Close confirmation dialog (main dialog will be closed by parent after save completes)
    this.confirmDialogVisible = false;
  }
}
